/**
 * The sidebar's icons: one set, drawn on a 20-unit grid with a 1.6 stroke
 * and round joins so they sit together, colored by the text around them
 * (`currentColor`), which is how the active item's icon turns with its label.
 *
 * Decorative: the label beside each one is the name, so every icon is hidden
 * from assistive technology. An icon is a required field of a NavItem; a
 * menu with icons on most rows reads as unfinished.
 */
import type { ReactNode } from 'react';

const ICONS = {
  // A checked list: what you owe.
  tasks: <><path d="M3.5 5.5l1.5 1.5 2.5-3M3.5 11l1.5 1.5L7.5 9.5M3.5 16h4" /><path d="M10.5 5.5h6M10.5 11h6M10.5 16h6" /></>,
  // Two documents, one behind the other.
  policies: <><path d="M6.5 5.5V4a1 1 0 0 1 1-1h5.6L16.5 6.4V14a1 1 0 0 1-1 1h-1.5" /><path d="M4.5 5.5h6l3 3V16a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1V6.5a1 1 0 0 1 1-1zM10.5 5.5v3h3" /></>,
  search: <><circle cx="9" cy="9" r="5.5" /><path d="M13.2 13.2L17 17" /></>,
  upload: <><path d="M10 13V3.5M6.5 7L10 3.5 13.5 7" /><path d="M3.5 12.5v3a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-3" /></>,
  // A seal with a check: a decision is yours to make.
  approvals: <><path d="M10 2.8l1.9 1.4 2.3-.1.7 2.2 1.9 1.4-.8 2.2.8 2.2-1.9 1.4-.7 2.2-2.3-.1L10 17l-1.9-1.4-2.3.1-.7-2.2-1.9-1.4.8-2.2-.8-2.2L5.1 6.3l.7-2.2 2.3.1z" /><path d="M7.3 10l1.9 1.9L12.8 8.2" /></>,
  // A hospital unit: a building with a cross.
  scope: <><path d="M4 17V5.5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1V17M2.5 17h15" /><path d="M10 7v4M8 9h4M8.5 17v-3h3v3" /></>,
  exceptions: <><path d="M10 3.5l7 12.5H3z" /><path d="M10 8.5v3.5M10 14.3v.2" /></>,
  // A rosette: a skill shown and signed off.
  competencies: <><circle cx="10" cy="8" r="4.5" /><path d="M7.4 11.8L6 17l4-2 4 2-1.4-5.2" /><path d="M8.3 8l1.2 1.2L11.8 7" /></>,
  forms: <><path d="M7 4H5.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H13" /><path d="M7 3h6v2.5H7zM7.5 9.5h5M7.5 12.5h5" /></>,
  inbox: <><path d="M3 11.5l2.2-6.8a1 1 0 0 1 1-.7h7.6a1 1 0 0 1 1 .7L17 11.5v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><path d="M3 11.5h4l1 2h4l1-2h4" /></>,
  // Steps joined in order.
  workflows: <><rect x="2.5" y="3.5" width="5" height="4" rx="1" /><rect x="12.5" y="3.5" width="5" height="4" rx="1" /><rect x="7.5" y="12.5" width="5" height="4" rx="1" /><path d="M7.5 5.5h5M15 7.5v2.5a1 1 0 0 1-1 1h-3a1 1 0 0 0-1 1v.5" /></>,
  taxonomy: <><path d="M3 10.2V4a1 1 0 0 1 1-1h6.2a1 1 0 0 1 .7.3l6 6a1 1 0 0 1 0 1.4l-6.2 6.2a1 1 0 0 1-1.4 0l-6-6a1 1 0 0 1-.3-.7z" /><circle cx="7" cy="7" r="1.1" /></>,
  staff: <><circle cx="7.5" cy="7" r="3" /><path d="M2 16.5c.4-3 2.6-4.7 5.5-4.7s5.1 1.7 5.5 4.7" /><path d="M13 4.3a3 3 0 0 1 0 5.4M15.2 12.4c1.6.7 2.6 2.1 2.8 4.1" /></>,
  // Into the tray: a library brought in.
  importLibrary: <><path d="M10 3.5V13M6.5 9.5L10 13l3.5-3.5" /><path d="M3.5 12.5v3a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-3" /></>,
  // Read and signed.
  acknowledgements: <><path d="M4.5 3.5h8l3 3V16a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" /><path d="M7 11.5l2 2 4-4.2" /></>,
  overdue: <><circle cx="10" cy="10" r="7" /><path d="M10 6v4.3l2.8 1.7" /></>,
  organization: <><path d="M3.5 17V4.5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1V17M11.5 8h4a1 1 0 0 1 1 1v8M2 17h16" /><path d="M6 7h3M6 10h3M6 13h3M14 11.5v.2M14 14.3v.2" /></>,
  // A ledger with its time.
  audit: <><path d="M9 17H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v4.5" /><path d="M7 7h4M7 10h2.5" /><circle cx="13.5" cy="14" r="3.5" /><path d="M13.5 12.4V14l1.1.8" /></>,
  // Out of the tray: the library taken away again. The mirror of importLibrary.
  export: <><path d="M10 12.5V3M6.5 6.5L10 3l3.5 3.5" /><path d="M3.5 12.5v3a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-3" /></>,
  settings: <><path d="M3 6h7M14 6h3M3 14h3M10 14h7" /><circle cx="12" cy="6" r="2" /><circle cx="8" cy="14" r="2" /></>,
  help: <><circle cx="10" cy="10" r="7" /><path d="M7.8 8a2.3 2.3 0 1 1 3.4 2c-.8.5-1.2 1-1.2 1.9M10 14.3v.2" /></>,
} as const satisfies Record<string, ReactNode>;

export type NavIconName = keyof typeof ICONS;
export const NAV_ICON_NAMES = Object.keys(ICONS) as NavIconName[];

export function NavIcon({ name, className }: { name: NavIconName; className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {ICONS[name]}
    </svg>
  );
}
